/**
 * The plan's player cap, as the API enforces it on create
 * (PLAN_PLAYER_LIMITS in lambda/routes/players.ts). Archived players don't
 * count; active and inactive ones do. Keep the numbers in step with the API.
 */
import type { Player } from '../api/client';
import type { Plan } from '../contexts/AuthContext';

const LIMITS: Record<Plan, number> = { free: 30, club: 100, pro: Number.POSITIVE_INFINITY };
const LABELS: Record<Plan, string> = { free: 'Free', club: 'Club', pro: 'Pro' };

export interface PlayerRoom {
  limit: number;
  used: number;
  left: number;
  full: boolean;
  capped: boolean;
  planLabel: string;
  /** "Upgrade to Club for up to 100 players", or '' on Pro. */
  upgradeLine: string;
}

/** `apiLimit` is the club's real cap from the players list (it can be above its
 *  plan's, set by us); null is unlimited, undefined falls back to the plan. */
export function playerRoom(plan: Plan, players: Player[], apiLimit?: number | null): PlayerRoom {
  const limit = apiLimit === undefined ? (LIMITS[plan] ?? LIMITS.free)
    : apiLimit === null ? Number.POSITIVE_INFINITY : apiLimit;
  const used = players.filter(p => p.status !== 'archived').length;
  return {
    limit,
    used,
    left: Math.max(0, limit - used),
    full: used >= limit,
    capped: Number.isFinite(limit),
    planLabel: LABELS[plan] ?? LABELS.free,
    upgradeLine: !Number.isFinite(limit) ? ''
      : limit < LIMITS.club ? `Upgrade to Club for up to ${LIMITS.club} players`
      : 'Upgrade to Pro for unlimited players',
  };
}

/** The API's refusal when a create would go over the cap. */
export const isPlayerLimitError = (e: unknown) =>
  e instanceof Error && /plan allows up to \d+ active players/i.test(e.message);
